import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../../components/common/PageHeader";
import { StatCard } from "../../components/common/StatCard";
import { PosService } from "../../services";
import { useAuth } from "../../store/AuthContext";
import { currency, dateTime } from "../../utils/format";
import {
  currentBusinessDate,
  businessDateLabel,
  isInBusinessDay,
  isSalesBlockedNow,
} from "../../utils/businessDay";
import { StatusBadge } from "../../components/common/StatusBadge";
import {
  ShoppingCart,
  Wallet,
  ArrowLeftRight,
  Banknote,
  CreditCard,
  PackageSearch,
} from "lucide-react";

const getSaleBusinessDateTime = (sale) =>
  sale?.saleDateTime || sale?.saleDatetime || sale?.createdAt;

const formatBusinessDateForDisplay = (businessDate) => {
  if (!businessDate) {
    return "selected date";
  }

  return businessDateLabel(businessDate);
};

const isMeasuredBottleSale = (item) =>
  item?.itemType === "BOTTLE" &&
  item?.barProductType !== "CIGARETTE" &&
  item?.bottleSaleMode &&
  item.bottleSaleMode !== "FULL_BOTTLE";

const getBottleCountLabel = (item) => {
  if (item?.barProductType === "CIGARETTE") return "units";
  const name = String(item?.itemName || "").toLowerCase();

  if (name.includes("tin")) {
    return "tins";
  }

  return "bottles";
};

export const CashierDashboardPage = () => {
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salesBlocked, setSalesBlocked] = useState(isSalesBlockedNow());
  const [selectedBusinessDate, setSelectedBusinessDate] = useState(
    currentBusinessDate()
  );

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    Promise.all([
      PosService.cashCurrent(user.userId).catch(() => null),
      PosService.mySales(user.userId, selectedBusinessDate).catch(() => []),
    ]).then(([sessionResponse, salesResponse]) => {
      if (!mounted) {
        return;
      }

      setSession(sessionResponse);
      setSales(Array.isArray(salesResponse) ? salesResponse : []);
      setLoading(false);
    });

    return () => {
      mounted = false;
    };
  }, [user.userId, selectedBusinessDate]);

  useEffect(() => {
    const checkBlocked = () => setSalesBlocked(isSalesBlockedNow());

    checkBlocked();

    const interval = setInterval(checkBlocked, 30000);

    return () => clearInterval(interval);
  }, []);

  const businessDate = selectedBusinessDate;
  const selectedBusinessDateLabel = formatBusinessDateForDisplay(
    selectedBusinessDate
  );

  const selectedDateSales = useMemo(() => {
    return sales.filter(
      (sale) =>
        isInBusinessDay(getSaleBusinessDateTime(sale), businessDate) &&
        sale.status !== "CANCELLED"
    );
  }, [sales, businessDate]);

  const productSalesSummary = useMemo(() => {
    const summaryMap = new Map();

    selectedDateSales.forEach((sale) => {
      const saleItems = Array.isArray(sale.items) ? sale.items : [];

      saleItems.forEach((item) => {
        if (!item) {
          return;
        }

        const itemType = item.itemType === "BITE" ? "FOOD" : item.itemType;
        const isBottle = itemType === "BOTTLE";
        const measuredSale = isMeasuredBottleSale(item);
        const saleMode = isBottle
          ? item.bottleSaleMode || "FULL_BOTTLE"
          : itemType;
        const measureMl = measuredSale ? item.measureMl || "MEASURE" : "";
        const productName = item.itemName || "Unnamed item";

        const key = [
          itemType,
          item.itemId ?? "CUSTOM",
          productName,
          saleMode,
          measureMl,
        ].join("|");

        const current = summaryMap.get(key) || {
          key,
          itemType,
          itemId: item.itemId,
          productName,
          saleMode,
          measureMl: item.measureMl,
          quantitySold: 0,
          measureUnitsSold: 0,
          totalSales: 0,
          billCount: 0,
          group: isBottle ? "BAR" : "RESTAURANT",
          unitLabel: measuredSale
            ? "servings"
            : isBottle
              ? getBottleCountLabel(item)
              : "items",
        };

        current.quantitySold += Number(item.quantity || 0);
        current.measureUnitsSold += Number(item.measureUnits || 0);
        current.totalSales += Number(item.totalPrice || 0);
        current.billCount += 1;

        summaryMap.set(key, current);
      });
    });

    const rows = Array.from(summaryMap.values()).sort((a, b) => {
      if (b.quantitySold !== a.quantitySold) {
        return b.quantitySold - a.quantitySold;
      }

      return b.totalSales - a.totalSales;
    });

    return {
      rows,
      barRows: rows.filter((row) => row.group === "BAR"),
      restaurantRows: rows.filter((row) => row.group === "RESTAURANT"),
      totalQuantity: rows.reduce(
        (sum, row) => sum + Number(row.quantitySold || 0),
        0
      ),
      totalSales: round2(
        rows.reduce((sum, row) => sum + Number(row.totalSales || 0), 0)
      ),
    };
  }, [selectedDateSales]);

  const totals = useMemo(() => {
    const summary = {
      totalToday: 0,
      cashToday: 0,
      cardToday: 0,
      totalBarSales: 0,
      totalRestaurantSales: 0,

      overallBarCash: 0,
      overallRestaurantCash: 0,
      overallBarCard: 0,
      overallRestaurantCard: 0,

      splitBarCash: 0,
      splitRestaurantCash: 0,
      splitBarCard: 0,
      splitRestaurantCard: 0,
      splitBillCount: 0,
    };

    selectedDateSales.forEach((sale) => {
      const grandTotal = Number(sale.grandTotal ?? sale.grandTotalAmount ?? 0);
      const bottleTotal = Number(sale.totalBottleAmount ?? 0);
      const biteTotal = Number(sale.totalBiteAmount ?? 0);

      const payments = Array.isArray(sale.payments) ? sale.payments : [];

      const cashAmount = payments
        .filter((payment) => payment.paymentMethod === "CASH")
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

      const cardAmount = payments
        .filter((payment) => payment.paymentMethod === "CARD")
        .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

      const barRatio = grandTotal > 0 ? bottleTotal / grandTotal : 0;
      const restaurantRatio = grandTotal > 0 ? biteTotal / grandTotal : 0;

      const barCash = round2(cashAmount * barRatio);
      const restaurantCash = round2(cashAmount * restaurantRatio);
      const barCard = round2(cardAmount * barRatio);
      const restaurantCard = round2(cardAmount * restaurantRatio);

      summary.totalToday += grandTotal;
      summary.cashToday += cashAmount;
      summary.cardToday += cardAmount;
      summary.totalBarSales += bottleTotal;
      summary.totalRestaurantSales += biteTotal;

      summary.overallBarCash += barCash;
      summary.overallRestaurantCash += restaurantCash;
      summary.overallBarCard += barCard;
      summary.overallRestaurantCard += restaurantCard;

      const isSplitPayment = cashAmount > 0 && cardAmount > 0;

      if (isSplitPayment) {
        summary.splitBillCount += 1;
        summary.splitBarCash += barCash;
        summary.splitRestaurantCash += restaurantCash;
        summary.splitBarCard += barCard;
        summary.splitRestaurantCard += restaurantCard;
      }
    });

    return {
      totalToday: round2(summary.totalToday),
      cashToday: round2(summary.cashToday),
      cardToday: round2(summary.cardToday),
      totalBarSales: round2(summary.totalBarSales),
      totalRestaurantSales: round2(summary.totalRestaurantSales),

      overallBarCash: round2(summary.overallBarCash),
      overallRestaurantCash: round2(summary.overallRestaurantCash),
      overallBarCard: round2(summary.overallBarCard),
      overallRestaurantCard: round2(summary.overallRestaurantCard),

      splitBarCash: round2(summary.splitBarCash),
      splitRestaurantCash: round2(summary.splitRestaurantCash),
      splitBarCard: round2(summary.splitBarCard),
      splitRestaurantCard: round2(summary.splitRestaurantCard),
      splitBillCount: summary.splitBillCount,
    };
  }, [selectedDateSales]);

  return (
    <div>
      <PageHeader
        title={`Hi, ${user.fullName.split(" ")[0]} 👋`}
        subtitle={`Cashier snapshot for ${selectedBusinessDateLabel}.`}
        actions={
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
             

              <input
                type="date"
                value={selectedBusinessDate}
                onChange={(event) =>
                  setSelectedBusinessDate(event.target.value)
                }
                className="h-8 min-w-[145px] bg-transparent text-sm  text-slate-800 outline-none"
                data-testid="cashier-dashboard-date"
              />
            </div>

            {salesBlocked ? (
              <button
                disabled
                className="rounded-lg bg-slate-200 px-5 py-2.5 text-sm inline-flex items-center justify-center gap-2 text-slate-500 cursor-not-allowed"
                title="Sales are blocked after 2:10 AM. Please close your cash session."
              >
                <ShoppingCart className="h-4 w-4" />
                Sales Closed
              </button>
            ) : (
              <Link
                to="/cashier/bar-sales"
                className="bms-btn-accent inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm"
              >
                <ShoppingCart className="h-4 w-4" />
                New Sale
              </Link>
            )}

            <Link
              to="/cashier/cash-session"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-5 py-2.5 text-sm hover:bg-slate-50"
            >
              <Wallet className="h-4 w-4" />
              Cash Session
            </Link>
          </div>
        }
      />

      {salesBlocked && session && (
        <div className="bms-card mb-5 border border-amber-200 bg-amber-50 p-4 text-amber-800">
          <div className="font-semibold">
            Sales are closed for the business day.
          </div>

          <div className="mt-1 text-sm">
            Please close your open cash session before leaving.
          </div>

          <Link
            to="/cashier/cash-session"
            className="mt-2 inline-block text-sm font-medium text-navy-700 hover:underline"
          >
            Go to Cash Session →
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Bills"
          value={loading ? "…" : selectedDateSales.length}
          icon="Receipt"
          tone="navy"
          testId="cashier-stat-bills"
        />

        <StatCard
          label="Total Sales"
          value={loading ? "…" : currency(totals.totalToday)}
          icon="BadgeDollarSign"
          tone="gold"
          testId="cashier-stat-total"
        />

        <StatCard
          label="Cash"
          value={loading ? "…" : currency(totals.cashToday)}
          icon="Banknote"
          tone="emerald"
          testId="cashier-stat-cash"
        />

        <StatCard
          label="Card"
          value={loading ? "…" : currency(totals.cardToday)}
          icon="CreditCard"
          tone="sky"
          testId="cashier-stat-card"
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-12">
        <div className="xl:col-span-3">
          <div className="bms-card h-full p-5">
            <h3 className="font-display text-lg font-semibold">
              Sales Breakdown
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Bar and restaurant item totals for the selected business date.
            </p>

            <div className="mt-5 space-y-3">
              <SimpleAmountTile
                label="Bar Sales"
                value={loading ? "…" : currency(totals.totalBarSales)}
              />

              <SimpleAmountTile
                label="Restaurant Sales"
                value={loading ? "…" : currency(totals.totalRestaurantSales)}
              />
            </div>
          </div>
        </div>

        <div className="xl:col-span-9">
          <div className="bms-card h-full p-5">
            <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-display text-lg font-semibold">
                  Payment Division
                </h3>

                <p className="text-sm text-slate-500">
                  Easy view of how selected-date cash and card collections are
                  divided between bar and restaurant.
                </p>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <PaymentDivisionCard
                label="Bar Cash"
                value={loading ? "…" : currency(totals.overallBarCash)}
              />

              <PaymentDivisionCard
                label="Restaurant Cash"
                value={loading ? "…" : currency(totals.overallRestaurantCash)}
              />

              <PaymentDivisionCard
                label="Bar Card"
                value={loading ? "…" : currency(totals.overallBarCard)}
              />

              <PaymentDivisionCard
                label="Restaurant Card"
                value={loading ? "…" : currency(totals.overallRestaurantCard)}
              />
            </div>

            <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
              These totals are based on selected-date payments and are allocated
              according to the value of bar items and restaurant items in each
              completed bill.
            </div>
          </div>
        </div>
      </div>

      <div className="bms-card mt-6 p-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-display text-lg font-semibold">
              Product Sales Count
            </h3>

            <p className="text-sm text-slate-500">
              Quantity of each product sold on {selectedBusinessDateLabel}.
              Measured bottles show servings and measured units separately.
            </p>
          </div>

          <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
            <PackageSearch className="h-3.5 w-3.5" />
            {productSalesSummary.rows.length} product
            {productSalesSummary.rows.length === 1 ? "" : "s"}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
          <ProductSalesCountList
            title="Bar Products"
            empty="No bar products sold for this date."
            rows={productSalesSummary.barRows}
          />

          <ProductSalesCountList
            title="Restaurant Products"
            empty="No restaurant products sold for this date."
            rows={productSalesSummary.restaurantRows}
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div className="bms-card p-5">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">
                Cash session
              </h3>
              <p className="text-xs text-slate-500">Current live session</p>
            </div>

            <StatusBadge value={session ? "OPEN" : "CLOSED"} />
          </div>

          {session ? (
            <div className="mt-3 space-y-2 text-sm">
              <Row label="Opened at" value={dateTime(session.openedAt)} />

              <Row
                label="Opening cash"
                value={currency(session.openingCashAmount)}
              />

              <Row
                label="Cash sales so far"
                value={currency(session.cashSalesAmount)}
              />

              <Row
                label="Expected closing cash"
                value={currency(session.expectedClosingCash)}
                bold
              />

              <Link
                to="/cashier/cash-session"
                className="mt-2 inline-block text-sm text-navy-700 hover:underline"
              >
                Close session →
              </Link>
            </div>
          ) : (
            <div className="mt-3 text-sm text-slate-500">
              No open session.{" "}
              <Link
                to="/cashier/cash-session"
                className="text-navy-700 hover:underline"
              >
                Start your day
              </Link>
              .
            </div>
          )}
        </div>

        <div className="bms-card p-5">
          <h3 className="mb-2 font-display text-lg font-semibold">
            Bills for Selected Date
          </h3>

          {selectedDateSales.length === 0 ? (
            <div className="text-sm text-slate-500">
              No bills for the selected business date.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {selectedDateSales.slice(0, 6).map((sale) => (
                <li key={sale.saleId}>
                  <Link
                    to={`/cashier/sales/${sale.saleId}`}
                    className="-mx-2 flex items-center justify-between rounded px-2 py-2.5 hover:bg-slate-50/60"
                  >
                    <div>
                      <div className="font-medium text-slate-900">
                        {sale.saleCode || sale.billNumber}
                      </div>

                      <div className="text-xs text-slate-500">
                        {dateTime(getSaleBusinessDateTime(sale))}
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <StatusBadge value={sale.status} />

                      <div className="w-24 text-right font-display font-semibold">
                        {currency(sale.grandTotal ?? sale.grandTotalAmount)}
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {totals.splitBillCount > 0 && (
        <div className="bms-card mt-6 p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="font-display text-lg font-semibold">
                Split Payments for Selected Date
              </h3>

              <p className="text-sm text-slate-500">
                Split-payment bills only. This section helps you quickly
                understand how combined cash + card bills were divided.
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-600">
              <ArrowLeftRight className="h-3.5 w-3.5" />
              {totals.splitBillCount} split bill
              {totals.splitBillCount > 1 ? "s" : ""}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SplitPartCard
              title="Cash Part"
              total={
                loading
                  ? "…"
                  : currency(totals.splitBarCash + totals.splitRestaurantCash)
              }
              leftLabel="Bar"
              leftValue={loading ? "…" : currency(totals.splitBarCash)}
              rightLabel="Restaurant"
              rightValue={loading ? "…" : currency(totals.splitRestaurantCash)}
            />

            <SplitPartCard
              title="Card Part"
              total={
                loading
                  ? "…"
                  : currency(totals.splitBarCard + totals.splitRestaurantCard)
              }
              leftLabel="Bar"
              leftValue={loading ? "…" : currency(totals.splitBarCard)}
              rightLabel="Restaurant"
              rightValue={loading ? "…" : currency(totals.splitRestaurantCard)}
            />
          </div>

          <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-600">
            Full cash-only and full card-only bills are already included in the
            main totals above. This section is only a smaller summary for split
            payments.
          </div>
        </div>
      )}
    </div>
  );
};

const ProductSalesCountList = ({ title, rows, empty }) => {
  const shouldScroll = rows.length > 5;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center justify-between border-b border-slate-100 pb-3">
        <h4 className="font-display text-base font-semibold text-slate-900">
          {title}
        </h4>

        <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-600">
          {rows.length}
        </span>
      </div>

      {rows.length === 0 ? (
        <div className="py-6 text-sm text-slate-500">{empty}</div>
      ) : (
        <div
          className={`mt-3 ${
            shouldScroll ? "max-h-[360px] overflow-y-auto pr-1" : ""
          }`}
        >
          <ul className="divide-y divide-slate-100">
            {rows.map((row) => {
              const measured =
                row.itemType === "BOTTLE" &&
                row.saleMode &&
                row.saleMode !== "FULL_BOTTLE";

              return (
                <li
                  key={row.key}
                  className="flex items-start justify-between gap-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">
                      {row.productName}
                    </div>

                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                        {row.itemType === "BOTTLE"
                          ? measured
                            ? `${row.measureMl || "Measured"} ml`
                            : row.unitLabel
                          : row.itemType}
                      </span>

                      <span>{currency(row.totalSales)}</span>

                      {measured && (
                        <span>{Number(row.measureUnitsSold || 0)} units</span>
                      )}
                    </div>
                  </div>

                  <div className="shrink-0 text-right">
                    <div className="font-display text-lg font-bold text-slate-900">
                      {row.quantitySold}
                    </div>

                    <div className="text-[11px] font-medium text-slate-500">
                      {row.unitLabel}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

const SimpleAmountTile = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </div>

    <div className="mt-2 font-display text-[22px] font-bold text-slate-900">
      {value}
    </div>
  </div>
);

const PaymentDivisionCard = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </div>

    <div className="mt-2 font-display text-[22px] font-bold text-slate-900">
      {value}
    </div>
  </div>
);

const SplitPartCard = ({
  title,
  total,
  leftLabel,
  leftValue,
  rightLabel,
  rightValue,
}) => (
  <div className="rounded-xl border border-slate-200 bg-white p-4">
    <div className="flex items-center justify-between">
      <div className="text-sm font-semibold text-slate-900">{title}</div>
      <div className="font-display text-lg font-bold text-slate-900">
        {total}
      </div>
    </div>

    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
          {leftLabel}
        </div>
        <div className="mt-1 text-lg font-semibold text-slate-900">
          {leftValue}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">
          {rightLabel}
        </div>
        <div className="mt-1 text-lg font-semibold text-slate-900">
          {rightValue}
        </div>
      </div>
    </div>
  </div>
);

const Row = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span
      className={
        bold
          ? "font-display font-semibold text-slate-900"
          : "text-slate-800"
      }
    >
      {value}
    </span>
  </div>
);

const round2 = (value) => {
  return Number((Number(value || 0)).toFixed(2));
};
